import type { EnvironmentId, PullRequestDetail } from "@cmmd-forge/contracts";
import { ArrowLeftIcon, ExternalLinkIcon, FileDiffIcon } from "lucide-react";
import type { Ref } from "react";

import type { PullRequestAgentSelection } from "~/pullRequest/pullRequestSelectionContext";
import { Button } from "~/components/ui/button";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import { formatRelativeTimeLabel } from "~/timestampFormat";
import { PullRequestActions } from "./summary/PullRequestActions";
import { PullRequestCheckoutAction } from "./PullRequestHandoffActions";
import { PullRequestTitleEditor } from "./PullRequestTitleEditor";
import type { usePullRequestDetailPanelModel } from "./usePullRequestDetailPanelModel";

type PanelModel = ReturnType<typeof usePullRequestDetailPanelModel>;

export function PullRequestDetailHeader(props: {
  readonly detail: PullRequestDetail;
  readonly environmentId: EnvironmentId;
  readonly model: PanelModel;
  readonly editable: boolean;
  readonly agentSelection: PullRequestAgentSelection | null;
  readonly condensed: boolean;
  readonly condensedRowRef: Ref<HTMLElement>;
  readonly foldRef: Ref<HTMLElement>;
}) {
  const branchCopy = useCopyToClipboard();
  if (props.condensed) {
    return (
      <header
        ref={props.condensedRowRef}
        className="grid min-w-0 shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-border/60 bg-background px-4 py-2"
      >
        <div className="flex min-w-0 items-center gap-2 text-xs">
          <a
            className="shrink-0 font-medium text-muted-foreground hover:text-foreground hover:underline"
            href={props.detail.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            #{props.detail.number}
          </a>
          <span className="min-w-0 truncate font-medium">{props.detail.title}</span>
          <span className="hidden min-w-0 flex-1 items-center gap-1.5 font-mono text-[11px] text-muted-foreground sm:flex">
            <code className="max-w-[35%] truncate">{props.detail.baseBranch}</code>
            <ArrowLeftIcon aria-label="receives changes from" className="size-3 shrink-0" />
            <code className="min-w-0 truncate">{props.detail.headBranch}</code>
          </span>
        </div>
        <PullRequestHeaderActions {...props} />
      </header>
    );
  }
  return (
    <header ref={props.foldRef} className="shrink-0 border-b border-border/60 bg-background">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-x-3 px-4 pb-4 pt-3">
        <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          <span className="truncate">
            {props.detail.provider} · {props.detail.repository} #{props.detail.number}
          </span>
          <a
            className="shrink-0 rounded p-1 hover:bg-accent hover:text-foreground"
            href={props.detail.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open pull request on provider"
          >
            <ExternalLinkIcon className="size-3.5" />
          </a>
        </div>
        <PullRequestHeaderActions {...props} />
        <div className="mt-2 min-w-0">
          <PullRequestTitleEditor
            title={props.detail.title}
            editable={props.editable}
            pending={props.model.updateMutation.isPending}
            onSave={(title) => props.model.updatePullRequest({ title })}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            {props.detail.author === null ? "Unknown author" : props.detail.author.login} · updated{" "}
            {formatRelativeTimeLabel(props.detail.updatedAt)}
          </p>
        </div>
        <div className="mt-4 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <span className="flex min-w-0 flex-1 items-center gap-1.5 font-mono">
            <code className="max-w-[40%] truncate">{props.detail.baseBranch}</code>
            <ArrowLeftIcon aria-label="receives changes from" className="size-3.5 shrink-0" />
            <button
              type="button"
              className="min-w-0 truncate rounded px-1 py-0.5 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={branchCopy.isCopied ? "Branch name copied" : "Copy pull request branch"}
              onClick={() => branchCopy.copyToClipboard(props.detail.headBranch, undefined)}
            >
              {branchCopy.isCopied ? "Copied" : props.detail.headBranch}
            </button>
          </span>
          <span className="ml-auto inline-flex shrink-0 items-center gap-2 tabular-nums">
            <span className="inline-flex items-center gap-1">
              <FileDiffIcon className="size-3.5" />
              {props.detail.changedFiles} {props.detail.changedFiles === 1 ? "file" : "files"}
            </span>
            <span className="font-mono text-success-foreground">+{props.detail.additions}</span>
            <span className="font-mono text-destructive-foreground">-{props.detail.deletions}</span>
          </span>
        </div>
      </div>
      <PullRequestFreshnessNotice model={props.model} />
    </header>
  );
}

function PullRequestHeaderActions(
  props: Pick<
    Parameters<typeof PullRequestDetailHeader>[0],
    "agentSelection" | "detail" | "environmentId" | "model"
  >,
) {
  return (
    <div className="row-span-3 flex shrink-0 items-start gap-1.5">
      <PullRequestCheckoutAction />
      <PullRequestActions
        actions={props.model.actions}
        detail={props.detail}
        mergeMethods={props.model.mergeMethods}
        updateMethods={props.model.updateMethods}
        mergeMethod={props.model.selectedMergeMethod}
        updateMethod={props.model.selectedUpdateMethod}
        onMergeMethodChange={props.model.setMergeMethod}
        onUpdateMethodChange={props.model.setUpdateMethod}
        pending={props.model.actionMutation.isPending}
        error={props.model.actionMutation.error}
        onRun={props.model.runAction}
        refreshing={props.model.detailQuery.isFetching || props.model.activityQuery.isFetching}
        onRefresh={props.model.refreshPullRequest}
        selection={props.agentSelection}
      />
    </div>
  );
}

function PullRequestFreshnessNotice({ model }: { readonly model: PanelModel }) {
  return model.isDetailFresh ? null : (
    <div className="mt-3 flex items-center justify-between gap-2 rounded-md border border-warning-border bg-warning-surface px-3 py-2 text-xs text-warning-foreground">
      <span>Showing retained pull request data. Refresh to enable changes.</span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => void model.detailQuery.refetch()}
      >
        Refresh
      </Button>
    </div>
  );
}
